import { useEffect, useState } from 'react';
import { redirectBrowser } from '@/lib/browser-redirect';

/**
 * Full-page redirect whose busy flag outlives the call: the page is leaving,
 * so `isRedirecting` stays true until unload. Back from the target can restore
 * this page from the back/forward cache with the flag still set, so a
 * persisted `pageshow` clears it. A navigation the browser cancels (a
 * `beforeunload` "Stay") fires no event at all — the caller `reset`s then.
 */
export function useBrowserRedirect() {
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(function resetOnBackForwardCacheRestore() {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setIsRedirecting(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const redirect = (url: string) => {
    setIsRedirecting(true);
    try {
      redirectBrowser(url);
    } catch (error) {
      setIsRedirecting(false);
      throw error;
    }
  };

  const reset = () => setIsRedirecting(false);

  return { isRedirecting, redirect, reset };
}
